import { useAdvisorCopy } from '@/shared/hooks/useAdvisorCopy';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/**
 * A client's backend `status` encodes two things the UI shows separately:
 *   - Type: Prospect vs Client (Prospect ⇒ not yet a client).
 *   - Status: the lifecycle badge (In Progress / Active / Inactive /
 *     Requires Review / Former).
 * These helpers derive both from the single backend value.
 */

export type ClientType = 'Prospect' | 'Client';

export function deriveClientType(status: string): ClientType {
  return status.toUpperCase() === 'PROSPECT' ? 'Prospect' : 'Client';
}

type StatusTone = 'active' | 'progress' | 'inactive' | 'review' | 'former';

export interface ClientStatusDisplay {
  label: string;
  tone: StatusTone;
}

export function deriveClientStatus(status: string): ClientStatusDisplay {
  switch (status.toUpperCase()) {
    case 'ACTIVE':
      return { label: 'Active', tone: 'active' };
    case 'ONBOARDING':
    case 'PROSPECT':
      return { label: 'In Progress', tone: 'progress' };
    case 'INACTIVE':
      return { label: 'Inactive', tone: 'inactive' };
    case 'REQUIRES_REVIEW':
      return { label: 'Requires Review', tone: 'review' };
    case 'CLOSED':
      return { label: 'Former', tone: 'former' };
    default:
      return { label: 'Inactive', tone: 'inactive' };
  }
}

// Match the backtest report's status pill exactly (StrategyHeader.tsx): the
// base Badge shape (px-2 py-0.5 rounded-sm) + text-[13px] font-medium + a
// 1.5px dot. No forced h-7 / px-2.5 — those made these read taller and wider
// than the report pills.
const BADGE_BASE = 'flex w-fit items-center gap-1.5 text-[13px] font-medium';

// Type badge — Prospect (slate) vs Client (violet), the two-state relationship
// type. Kept visually distinct from the lifecycle status badge.
export function ClientTypeBadge({ status }: { status: string }) {
  const copy = useAdvisorCopy();
  const type = deriveClientType(status);
  const s =
    type === 'Prospect'
      ? {
          badge:
            'bg-slate-500/10 text-foreground dark:text-muted-foreground border-slate-500/20',
          dot: 'bg-slate-500 dark:bg-slate-400',
        }
      : {
          badge:
            'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/20',
          dot: 'bg-violet-500 dark:bg-violet-400',
        };
  return (
    <Badge variant="outline" className={cn(BADGE_BASE, s.badge)}>
      <span className={cn('h-1.5 w-1.5 rounded-full', s.dot)} />
      {copy(type)}
    </Badge>
  );
}

const STATUS_TONE: Record<StatusTone, { badge: string; dot: string }> = {
  active: {
    badge:
      'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20',
    dot: 'bg-emerald-500 dark:bg-emerald-400',
  },
  progress: {
    badge:
      'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20',
    dot: 'bg-amber-500 dark:bg-amber-400',
  },
  inactive: {
    badge:
      'bg-slate-500/10 text-muted-foreground dark:text-muted-foreground border-slate-500/20',
    dot: 'bg-slate-400 dark:bg-slate-500',
  },
  review: {
    badge: 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/20',
    dot: 'bg-red-500 dark:bg-red-400',
  },
  former: {
    badge: 'bg-muted text-muted-foreground border-border',
    dot: 'bg-muted-foreground/50',
  },
};

// Lifecycle status badge — Active / In Progress / Inactive / Requires Review /
// Former.
export function ClientStatusBadge({ status }: { status: string }) {
  const copy = useAdvisorCopy();
  const { label, tone } = deriveClientStatus(status);
  const s = STATUS_TONE[tone];
  return (
    <Badge variant="outline" className={cn(BADGE_BASE, s.badge)}>
      <span className={cn('h-1.5 w-1.5 rounded-full', s.dot)} />
      {copy(label)}
    </Badge>
  );
}
